import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ChevronDown } from "lucide-react";
import { api, type ChannelRating } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPips } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * M22 — every signal channel side by side, so "which of these is worth trading" is one
 * screen instead of a tab per channel.
 *
 * **This is a channel view, not an account view.** It reads every signal a channel produced,
 * whichever account executed it. A channel rated on paper for a month and then promoted to
 * live did not become a different channel on the day it moved, and a table that reset its
 * history at the promotion would be useless for the decision it exists to inform. The
 * overview and the reports are the account views; they filter to the live account and answer
 * "how did my money do".
 *
 * **Everything is in pips.** A simulated close has no deal and therefore no money figure, so
 * a money column would be empty on exactly the channels being evaluated.
 *
 * Rendering rules, inherited from `ChannelAnalytics` rather than reinvented:
 *
 * - **Null is a dash, never a zero.** No profit factor means nothing has lost yet; that is
 *   not an infinite edge, it is no evidence, and "0" or "∞" would both sort like a verdict.
 * - **A rate carries its denominator** — "60% of 5 decided", never a bare percentage.
 * - **An empty row explains itself.** `measurement` says whether the channel is trading, being
 *   rated, or switched off entirely, because "no numbers" otherwise looks the same in all three.
 *
 * Cards rather than a `<table>`: this is a phone-first PWA and eleven columns of tabular data
 * on a 390px screen is a horizontal scrollbar nobody reads. The sort control is what makes it
 * a comparison — pick the metric, the order answers the question.
 */

const WINDOWS = [30, 90, 365] as const;

type SortKey = "pipsRealized" | "expectancyPips" | "winRate" | "profitFactor" | "signalsClosed" | "honesty";

const SORTS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: "pipsRealized", label: "Net pips" },
  { key: "expectancyPips", label: "Per signal" },
  { key: "winRate", label: "Win rate" },
  { key: "profitFactor", label: "Profit factor" },
  { key: "signalsClosed", label: "Volume" },
  { key: "honesty", label: "Honesty" },
];

const MEASUREMENT: Record<ChannelRating["measurement"], { label: string; hint: string }> = {
  live: { label: "LIVE", hint: "Trading on the active account." },
  rated: { label: "RATING", hint: "Filling on paper against live prices. No order reaches a broker." },
  off: {
    label: "OFF",
    hint: "Not being measured — no account is subscribed with auto-execute on, so this channel will produce no new numbers.",
  },
};

/**
 * Honesty is a rank, so it needs one number. Fouls dominate — a deleted losing signal is a
 * deliberate act — and overclaimed pips break the ties between channels that have not been
 * caught doing anything outright. Lower is better, and a channel with no evidence either way
 * sorts as 0 rather than as clean.
 */
function dishonesty(row: ChannelRating): number {
  return row.foulCount * 1000 + Math.max(0, row.pipsOverclaimed ?? 0);
}

/**
 * Nulls sort last in every order, ascending included.
 *
 * Treating "unknown" as -Infinity would park the unproven channels at the bottom of a
 * descending sort, which reads as "these are the worst" when it means "these have no
 * evidence". Keeping them last in both directions makes the absence visible instead.
 */
function compare(a: ChannelRating, b: ChannelRating, key: SortKey): number {
  if (key === "honesty") return dishonesty(a) - dishonesty(b);
  const left = a[key];
  const right = b[key];
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return right - left;
}

export function ChannelComparison() {
  const [days, setDays] = useState<number>(30);
  const [sort, setSort] = useState<SortKey>("pipsRealized");

  const query = useQuery({
    queryKey: queryKeys.channelComparison(days),
    queryFn: () => api.channelComparison(days),
  });

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;
  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">Could not load the channel comparison.</p>;
  }

  const rows = [...query.data.channels].sort((a, b) => compare(a, b, sort));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {WINDOWS.map((option) => (
          <Button
            key={option}
            size="sm"
            variant={option === days ? "default" : "outline"}
            onClick={() => setDays(option)}
          >
            {option} days
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Rank by</span>
        {SORTS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setSort(option.key)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-xs transition-colors",
              option.key === sort
                ? "border-primary bg-primary/10 text-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      {rows.length === 0 && <p className="text-sm text-muted-foreground">No signal channels yet.</p>}

      <div className="space-y-2">
        {rows.map((row) => (
          <RatingCard key={row.channelId} row={row} sort={sort} />
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        {query.data.from} → {query.data.to}, every account. A channel keeps its history when it
        moves from rating to live — it is the same channel. Pips, not money: a paper fill has no
        deal behind it and so no money figure.
      </p>
    </div>
  );
}

function RatingCard({ row, sort }: { row: ChannelRating; sort: SortKey }) {
  const [open, setOpen] = useState(false);
  // Won or lost, not counting breakeven — the same denominator `ChannelAnalytics` uses, so
  // one channel cannot show two win rates on two screens.
  const decided = row.wins + row.losses;
  const measurement = MEASUREMENT[row.measurement];

  return (
    <Card>
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <Link to={`/channels/${row.channelId}`} className="truncate text-sm font-medium hover:underline">
              {row.channelTitle}
            </Link>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant={row.measurement === "live" ? "default" : "outline"}>{measurement.label}</Badge>
              {row.foulCount > 0 && <Badge variant="destructive">🚩 {row.foulCount}</Badge>}
              <span className="text-xs text-muted-foreground">{row.signalsClosed} closed</span>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-lg font-semibold tabular-nums">{formatPips(row.pipsRealized)}</div>
            <div className="text-xs text-muted-foreground tabular-nums">
              {/* The denominator travels with the rate. "60%" from five trades is the claim
                  signal channels make about themselves; this is the fact. */}
              {row.winRate === null ? "—" : `${row.winRate}% of ${decided} decided`}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <Stat label="Per signal" value={formatPips(row.expectancyPips)} highlight={sort === "expectancyPips"} />
          <Stat
            label="Profit factor"
            value={row.profitFactor === null ? "—" : row.profitFactor.toFixed(2)}
            highlight={sort === "profitFactor"}
          />
          <Stat label="Overclaimed" value={formatPips(row.pipsOverclaimed)} highlight={sort === "honesty"} />
        </div>

        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className="flex w-full items-center justify-center gap-1 text-xs text-muted-foreground"
          aria-expanded={open}
        >
          {open ? "Less" : "More"}
          <ChevronDown className={cn("h-3 w-3 transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="space-y-2 border-t border-border pt-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <Row label="Average win" value={formatPips(row.avgWinPips)} />
              <Row label="Average loss" value={formatPips(row.avgLossPips)} />
              {/* Unsigned: a drawdown is pain, and a "+" in front of it reads as a gain. */}
              <Row
                label="Max drawdown"
                value={row.maxDrawdownPips === null ? "—" : `${row.maxDrawdownPips.toFixed(1)} pips`}
              />
              <Row label="Average hold" value={formatHold(row.avgHoldMinutes)} />
              {/* M28b — worded, not signed: "-3" under a small label invites misreading. */}
              <Row
                label="Streak now"
                value={
                  row.currentStreak === 0
                    ? "—"
                    : row.currentStreak > 0
                      ? `${row.currentStreak} ${row.currentStreak === 1 ? "win" : "wins"} in a row`
                      : `${-row.currentStreak} ${row.currentStreak === -1 ? "loss" : "losses"} in a row`
                }
              />
              <Row
                label="Longest runs"
                value={
                  row.longestWinStreak === 0 && row.longestLossStreak === 0
                    ? "—"
                    : `${row.longestWinStreak} ${row.longestWinStreak === 1 ? "win" : "wins"} · ${row.longestLossStreak} ${row.longestLossStreak === 1 ? "loss" : "losses"}`
                }
              />
              <Row label="They claimed" value={formatPips(row.pipsClaimed)} />
              <Row label="We realized" value={formatPips(row.pipsRealized)} />
              <Row
                label="Reliability"
                value={row.reliabilityScore === null ? "—" : `${row.reliabilityScore}/100`}
              />
              <Row label="Shady level" value={row.shadyLevel === null ? "—" : `${row.shadyLevel}/10`} />
            </div>
            <p className="text-xs text-muted-foreground">{measurement.hint}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight: boolean }) {
  return (
    <div className={cn("rounded-md p-1.5", highlight && "bg-primary/10")}>
      <div className="text-muted-foreground">{label}</div>
      <div className="tabular-nums">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/** Minutes are unreadable past a couple of hours, and a hold time is never worth a second. */
function formatHold(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 90) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}
